import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Resource, FilterState } from './types';
import Filters from './Filters.tsx';
import ResourceGrid from './ResourceGrid.tsx';
// import Loading from './Loading-Deprecated.tsx';
import LoadingMessage from '../../components/LoadingMessage.js';
import ErrorMessage from './ErrorMessage.tsx';

const ResourcesPage: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state
  const [filterState, setFilterState] = useState<FilterState>({
    selectedTypes: [],
    selectedSeries: [],
    searchTerm: ''
  });
  
  // Get unique values for filters
  const uniqueTypes = [...new Set(resources.filter(r => r.type.length > 0).map(r => r.type[0].toUpperCase() + r.type.slice(1)))].sort();
  const uniqueSeries = [...new Set(resources.filter(r => r.series.length > 0).map(r => r.series[0].toUpperCase() + r.series.slice(1)))].sort();

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '32px 16px 24px',
      overflowY: 'auto' as const,
      maxHeight: 'calc(100vh - 56px)',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: '#e0e0e0',
      marginBottom: '24px'
    }
  };

  // Fetch resources from API
  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);         
        const response = await axios.get<Resource[]>(`${process.env.REACT_APP_API_HOST}/resources/general`);
        setResources(response.data.filter(r => r.type !== 'coaching'));
        setFilteredResources(response.data.filter(r => r.type !== 'coaching'));
      } catch (err) {
        setError('Failed to fetch resources. Please try again later.');
        console.error('Error fetching resources:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  // Apply filters whenever filter criteria change
  useEffect(() => {
    let filtered = resources;

    if (filterState.selectedTypes.length > 0) {
      filtered = filtered.filter(resource => filterState.selectedTypes.map(t => t.toLowerCase()).includes(resource.type.toLowerCase()));
    }

    if (filterState.selectedSeries.length > 0) {
      filtered = filtered.filter(resource => filterState.selectedSeries.map(s => s.toLowerCase()).includes(resource.series.toLowerCase()));
    }

    if (filterState.searchTerm) {
      filtered = filtered.filter(resource =>
        resource.filename.toLowerCase().includes(filterState.searchTerm.toLowerCase()) ||
        resource.type.toLowerCase().includes(filterState.searchTerm.toLowerCase()) ||
        resource.series.toLowerCase().includes(filterState.searchTerm.toLowerCase())
      );
    }

    setFilteredResources(filtered);
  }, [resources, filterState]);

  const clearFilters = () => {
    setFilterState({
      selectedTypes: [],
      selectedSeries: [],
      searchTerm: ''
    });
  };

  const handleOpenFile = (url: string) => {
    window.open(url, '_blank');
  };

  if (loading) return <LoadingMessage />;

  if (error) {
    return (
      <div style={styles.container}>
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Resources</h1>

      <Filters
        filterState={filterState}
        uniqueTypes={uniqueTypes}
        uniqueSeries={uniqueSeries}
        totalResources={resources.length}
        filteredCount={filteredResources.length}
        onFilterChange={setFilterState}
        onClearFilters={clearFilters}
      />

      <ResourceGrid
        resources={filteredResources}
        onOpenFile={handleOpenFile}
      />
    </div>
  );
};

export default ResourcesPage;